import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { trackPhoneClick, trackWhatsAppClick } from "../utils/analytics";

const sections = [
  {
    title: "What Data We Collect",
    items: [
      "Name, if you choose to provide it in a booking message or follow-up communication",
      "Phone number",
      "Pickup location",
      "Destination",
      "Booking date and time",
      "Flight number, if provided",
      "Passenger and luggage details",
    ],
  },
  {
    title: "Why We Collect Data",
    items: [
      "To arrange taxi bookings",
      "To contact you about your booking",
      "To manage airport transfer requests",
    ],
  },
  {
    title: "Data Storage",
    items: [
      "Bookings are stored securely in Supabase.",
      "Booking notifications may be sent through Resend from our Supabase Edge Function.",
    ],
  },
  {
    title: "Data Sharing",
    items: [
      "We do not sell personal data.",
      "Booking details are used only for booking management and customer communication.",
    ],
  },
  {
    title: "Data Retention",
    items: [
      "Booking records are kept only as long as needed for business and admin purposes.",
    ],
  },
  {
    title: "Your Rights",
    items: [
      "You can request access to your booking data.",
      "You can request correction of inaccurate booking data.",
      "You can request deletion of booking data when it is no longer needed for business or legal purposes.",
    ],
  },
];

function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-[#030712] px-6 py-16 text-white">
      <Helmet>
        <title>Privacy Policy | Limassol Airport Taxi</title>
        <meta
          name="description"
          content="Privacy Policy for Limassol Airport Taxi booking and airport transfer services in Cyprus."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Privacy Policy | Limassol Airport Taxi" />
        <meta
          property="og:description"
          content="Privacy Policy for Limassol Airport Taxi booking and airport transfer services in Cyprus."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://limassolairporttaxi.com/privacy-policy" />
        <link rel="canonical" href="https://limassolairporttaxi.com/privacy-policy" />
      </Helmet>

      <section className="mx-auto max-w-4xl">
        <Link
          to="/"
          className="inline-flex rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-sm font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
        >
          Back to Home
        </Link>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl shadow-black/30 sm:p-10">
          <p className="text-sm font-bold uppercase tracking-wide text-yellow-400">
            Limassol Airport Taxi
          </p>

          <h1 className="mt-3 text-4xl font-black text-yellow-400 sm:text-5xl">
            Privacy Policy
          </h1>

          <p className="mt-5 leading-8 text-neutral-300">
            This Privacy Policy explains how Limassol Airport Taxi collects and uses
            booking information when you request taxi or airport transfer services
            through this website, phone, or WhatsApp.
          </p>
        </div>

        <div className="mt-8 grid gap-5">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-3xl border border-white/10 bg-black/30 p-6 sm:p-8"
            >
              <h2 className="text-2xl font-bold text-yellow-400">
                {section.title}
              </h2>

              <ul className="mt-4 space-y-3 text-neutral-300">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-3 leading-7">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-yellow-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-3xl border border-yellow-400/20 bg-yellow-400/10 p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-yellow-400">
            Contact
          </h2>

          <p className="mt-4 leading-8 text-neutral-200">
            To request access, correction, or deletion of your booking details,
            contact Limassol Airport Taxi by email, phone, or WhatsApp.
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href="mailto:info@limassolairporttaxi.com"
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
            >
              Email Us
            </a>

            <a
              href="tel:+35796613191"
              onClick={() => trackPhoneClick("privacy_policy_phone")}
              className="rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-black transition hover:bg-yellow-300"
            >
              Call +357 96 613 191
            </a>

            <a
              href="https://wa.me/35796613191?text=Hello,%20I%20have%20a%20privacy%20request."
              onClick={() => trackWhatsAppClick("privacy_policy_whatsapp")}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 text-center font-bold text-white transition hover:border-yellow-400/50 hover:text-yellow-300"
            >
              WhatsApp
            </a>
          </div>
        </section>
      </section>
    </main>
  );
}

export default PrivacyPolicyPage;
